import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  handleStatusFilterOptions,
  PRIORITIES_FILTER_OPTIONS,
  QC_FILTER_OPTIONS,
} from "../../../utils/constants/index";
import DateRangeFilter from "../../common/DateRangeFilter";
import FilterSelect from "../../common/FilterSelect";
import ActiveFilterChips from "../../common/ActiveFilterChips";
import { Input } from "../../ui/input";
import RequestFiltersPanel from "./RequestFiltersPanel";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {
  EMPTY_PANEL_FILTERS,
  panelFromUrl,
} from "../../../utils/constants/AdRequest";
import {
  SOURCE_FILTER_OPTIONS,
  dateParam,
  day,
  optionLabel,
  peopleLabel,
  valuesLabel,
} from "../../../utils/constants/listFilters";

/** Ad Request filters: bar, panel and chips, mirrored into the URL; reports API params via onChange. */
const RequestFilterBar = ({
  agencyScoped,
  showAgencyFilter,
  agencyOptions,
  defaultAgencyId,
  onChange,
  actions,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(() => searchParams.get("status") || "");
  const [panel, setPanel] = useState(() => panelFromUrl(searchParams));
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("query") || ""
  );
  const searchQuery = useDebouncedValue(searchInput, 300);
  const [raised, setRaised] = useState(() => ({
    from: dateParam(searchParams, "date_from"),
    to: dateParam(searchParams, "date_to"),
  }));
  const [launched, setLaunched] = useState(() => ({
    from: dateParam(searchParams, "launched_from"),
    to: dateParam(searchParams, "launched_to"),
  }));

  const apiParams = {
    status: status || undefined,
    priority: panel.priority || undefined,
    qc_status: panel.qcStatus || undefined,
    requested_by_id: panel.requestedBy || undefined,
    requested_via: panel.source || undefined,
    search: searchQuery.trim() || undefined,
    date_from: day(raised.from) || undefined,
    date_to: day(raised.to) || undefined,
    launched_from: day(launched.from) || undefined,
    launched_to: day(launched.to) || undefined,
    category: panel.categories.join(",") || undefined,
  };
  const filterKey = JSON.stringify([apiParams, panel.agency]);

  useEffect(() => {
    const [params, agency] = JSON.parse(filterKey);
    onChange({ apiParams: params, agency, key: filterKey });
  }, [filterKey, onChange]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const setOrDelete = (key, val) =>
          val ? next.set(key, val) : next.delete(key);
        setOrDelete("status", status);
        setOrDelete("priority", panel.priority);
        setOrDelete("qc_status", panel.qcStatus);
        if (!agencyScoped) setOrDelete("agency", panel.agency);
        setOrDelete("requested_by", panel.requestedBy);
        setOrDelete("source", panel.source);
        setOrDelete("category", panel.categories.join(","));
        setOrDelete("query", searchQuery.trim());
        setOrDelete("date_from", day(raised.from));
        setOrDelete("date_to", day(raised.to));
        setOrDelete("launched_from", day(launched.from));
        setOrDelete("launched_to", day(launched.to));
        return next;
      },
      { replace: true }
    );
  }, [
    status,
    panel,
    searchQuery,
    raised,
    launched,
    agencyScoped,
    setSearchParams,
  ]);

  const clearPanel = (keys) =>
    setPanel((p) => ({
      ...p,
      ...Object.fromEntries(keys.map((k) => [k, EMPTY_PANEL_FILTERS[k]])),
    }));
  const chip = (key, label, keys = [key]) => ({
    key,
    label,
    onRemove: () => clearPanel(keys),
  });

  const chips = [
    panel.categories.length > 0 &&
      chip("categories", `Category: ${valuesLabel(panel.categories)}`),
    panel.priority &&
      chip(
        "priority",
        `Priority: ${optionLabel(PRIORITIES_FILTER_OPTIONS, panel.priority)}`
      ),
    panel.qcStatus &&
      chip("qcStatus", `QC: ${optionLabel(QC_FILTER_OPTIONS, panel.qcStatus)}`),
    panel.source &&
      chip(
        "source",
        `Source: ${optionLabel(SOURCE_FILTER_OPTIONS, panel.source)}`
      ),
    showAgencyFilter &&
      panel.agency &&
      chip("agency", `Agency: ${optionLabel(agencyOptions, panel.agency)}`),
    panel.requestedBy &&
      chip(
        "requestedBy",
        `Requested by: ${peopleLabel(panel.requestedByLabels, panel.requestedBy)}`,
        ["requestedBy", "requestedByLabels"]
      ),
  ].filter(Boolean);

  const hasAnyFilter = !!(
    chips.length ||
    status ||
    raised.from ||
    raised.to ||
    launched.from ||
    launched.to ||
    searchInput
  );

  const clearAll = () => {
    setPanel(EMPTY_PANEL_FILTERS);
    setStatus("");
    setRaised({ from: null, to: null });
    setLaunched({ from: null, to: null });
    setSearchInput("");
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search by topic, Ad ID or comment…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="min-w-[220px] flex-1 h-9 bg-white border-gray-300"
        />
        <FilterSelect
          value={status}
          allLabel="All statuses"
          placeholder="All statuses"
          options={handleStatusFilterOptions({ isOnRequestPage: true })}
          onChange={setStatus}
        />
        <DateRangeFilter
          label="Raised Date"
          from={raised.from}
          to={raised.to}
          onChange={(from, to) => setRaised({ from, to })}
        />
        <DateRangeFilter
          label="Launched"
          from={launched.from}
          to={launched.to}
          onChange={(from, to) => setLaunched({ from, to })}
        />
        <RequestFiltersPanel
          applied={panel}
          onApply={setPanel}
          count={chips.length}
          showAgencyFilter={showAgencyFilter}
          agencyOptions={agencyOptions}
          defaultAgencyId={defaultAgencyId}
        />
        {actions}
      </div>
      {hasAnyFilter && (
        <ActiveFilterChips chips={chips} onClearAll={clearAll} />
      )}
    </>
  );
};

export default RequestFilterBar;
